import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check, X, ChevronDown, Search, Camera, Pipette, Eye, EyeOff, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Utility to generate slug from name
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

// Simulated taken slugs - now using real API
const TAKEN_SLUGS = []; // Not used anymore

const BOUTIQUE_TYPES = [
  { id: "restaurant", icon: "🍽️", label: "Restaurant / Snack", desc: "Plats, menus, fast-food", family: "caisse" },
  { id: "bar", icon: "🍺", label: "Bar / Café", desc: "Boissons, cocktails, snacks", family: "caisse" },
  { id: "epicerie", icon: "🛒", label: "Épicerie / Alimentation", desc: "Produits frais, épicerie", family: "caisse" },
  { id: "boissons", icon: "🍷", label: "Cave & Boissons", desc: "Vin, bière, liqueurs, sucreries", family: "caisse" },
  { id: "general", icon: "🏪", label: "Commerce général", desc: "Tout type d'articles", family: "caisse" },
  { id: "femme", icon: "👗", label: "Mode femme", desc: "Prêt-à-porter, robes, accessoires", family: "mode" },
  { id: "homme", icon: "👔", label: "Mode homme", desc: "Chemises, costumes, casual", family: "mode" },
  { id: "enfant", icon: "👶", label: "Mode enfant", desc: "Bébé, filles, garçons", family: "mode" },
  { id: "sneakers", icon: "👟", label: "Sneakers & Accès.", desc: "Chaussures, casquettes", family: "mode" },
  { id: "bijoux", icon: "💍", label: "Bijoux & Montres", desc: "Or, argent, fantaisie", family: "mode" },
  { id: "multi", icon: "🛍️", label: "Multi-catégories", desc: "Plusieurs univers mode", family: "mode" },
];

const TYPE_FAMILIES = [
  { id: "caisse", label: "Caisse universelle", desc: "Restaurant, bar, épicerie, cave…" },
  { id: "mode", label: "Boutique mode", desc: "Variantes taille/couleur, vitrine, étiquettes" },
];

// Countries with currencies
const COUNTRIES = [
  { code: "CI", name: "Côte d'Ivoire", flag: "🇨🇮", currency: "XOF", currencySymbol: "F CFA", currencyName: "Franc CFA (BCEAO)", group: "Afrique francophone", phoneCode: "+225" },
  { code: "SN", name: "Sénégal", flag: "🇸🇳", currency: "XOF", currencySymbol: "F CFA", currencyName: "Franc CFA (BCEAO)", group: "Afrique francophone", phoneCode: "+221" },
  { code: "CM", name: "Cameroun", flag: "🇨🇲", currency: "XAF", currencySymbol: "F CFA", currencyName: "Franc CFA (BEAC)", group: "Afrique francophone", phoneCode: "+237" },
  { code: "BJ", name: "Bénin", flag: "🇧🇯", currency: "XOF", currencySymbol: "F CFA", currencyName: "Franc CFA (BCEAO)", group: "Afrique francophone", phoneCode: "+229" },
  { code: "ML", name: "Mali", flag: "🇲🇱", currency: "XOF", currencySymbol: "F CFA", currencyName: "Franc CFA (BCEAO)", group: "Afrique francophone", phoneCode: "+223" },
  { code: "BF", name: "Burkina Faso", flag: "🇧🇫", currency: "XOF", currencySymbol: "F CFA", currencyName: "Franc CFA (BCEAO)", group: "Afrique francophone", phoneCode: "+226" },
  { code: "TG", name: "Togo", flag: "🇹🇬", currency: "XOF", currencySymbol: "F CFA", currencyName: "Franc CFA (BCEAO)", group: "Afrique francophone", phoneCode: "+228" },
  { code: "GN", name: "Guinée", flag: "🇬🇳", currency: "GNF", currencySymbol: "GNF", currencyName: "Franc guinéen", group: "Afrique francophone", phoneCode: "+224" },
  { code: "MG", name: "Madagascar", flag: "🇲🇬", currency: "MGA", currencySymbol: "Ar", currencyName: "Ariary malgache", group: "Afrique francophone", phoneCode: "+261" },
  { code: "CD", name: "RD Congo", flag: "🇨🇩", currency: "CDF", currencySymbol: "FC", currencyName: "Franc congolais", group: "Afrique francophone", phoneCode: "+243" },
  { code: "FR", name: "France", flag: "🇫🇷", currency: "EUR", currencySymbol: "€", currencyName: "Euro", group: "Europe", phoneCode: "+33" },
  { code: "BE", name: "Belgique", flag: "🇧🇪", currency: "EUR", currencySymbol: "€", currencyName: "Euro", group: "Europe", phoneCode: "+32" },
  { code: "CH", name: "Suisse", flag: "🇨🇭", currency: "CHF", currencySymbol: "CHF", currencyName: "Franc suisse", group: "Europe", phoneCode: "+41" },
  { code: "MA", name: "Maroc", flag: "🇲🇦", currency: "MAD", currencySymbol: "DH", currencyName: "Dirham marocain", group: "Maghreb", phoneCode: "+212" },
  { code: "DZ", name: "Algérie", flag: "🇩🇿", currency: "DZD", currencySymbol: "DA", currencyName: "Dinar algérien", group: "Maghreb", phoneCode: "+213" },
  { code: "TN", name: "Tunisie", flag: "🇹🇳", currency: "TND", currencySymbol: "DT", currencyName: "Dinar tunisien", group: "Maghreb", phoneCode: "+216" },
  { code: "US", name: "États-Unis", flag: "🇺🇸", currency: "USD", currencySymbol: "$", currencyName: "Dollar américain", group: "Autres", phoneCode: "+1" },
  { code: "GB", name: "Royaume-Uni", flag: "🇬🇧", currency: "GBP", currencySymbol: "£", currencyName: "Livre sterling", group: "Autres", phoneCode: "+44" },
  { code: "CA", name: "Canada", flag: "🇨🇦", currency: "CAD", currencySymbol: "$", currencyName: "Dollar canadien", group: "Autres", phoneCode: "+1" },
  { code: "NG", name: "Nigeria", flag: "🇳🇬", currency: "NGN", currencySymbol: "₦", currencyName: "Naira", group: "Autres", phoneCode: "+234" },
  { code: "GH", name: "Ghana", flag: "🇬🇭", currency: "GHS", currencySymbol: "GH₵", currencyName: "Cedi ghanéen", group: "Autres", phoneCode: "+233" },
];

const CURRENCIES = [
  { code: "XOF", symbol: "F CFA", name: "Franc CFA (BCEAO)" },
  { code: "XAF", symbol: "F CFA", name: "Franc CFA (BEAC)" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "USD", symbol: "$", name: "Dollar américain" },
  { code: "GBP", symbol: "£", name: "Livre sterling" },
  { code: "CHF", symbol: "CHF", name: "Franc suisse" },
  { code: "MAD", symbol: "DH", name: "Dirham marocain" },
  { code: "DZD", symbol: "DA", name: "Dinar algérien" },
  { code: "TND", symbol: "DT", name: "Dinar tunisien" },
  { code: "GNF", symbol: "GNF", name: "Franc guinéen" },
  { code: "MGA", symbol: "Ar", name: "Ariary malgache" },
  { code: "CDF", symbol: "FC", name: "Franc congolais" },
  { code: "NGN", symbol: "₦", name: "Naira" },
  { code: "GHS", symbol: "GH₵", name: "Cedi ghanéen" },
  { code: "CAD", symbol: "$", name: "Dollar canadien" },
];

// Color palettes for step 3
const COLOR_PALETTES = [
  { id: "luxe", name: "Luxe Noir + Or", primary: "#D4AF37", secondary: "#0A0A0A" },
  { id: "nude", name: "Rose Nude", primary: "#E8B4B8", secondary: "#FFF5F5" },
  { id: "emerald", name: "Émeraude", primary: "#1A7A4A", secondary: "#F0FFF4" },
  { id: "bordeaux", name: "Bordeaux", primary: "#8B1A1A", secondary: "#FFF5F5" },
  { id: "navy", name: "Bleu Nuit", primary: "#1A2A5E", secondary: "#F0F4FF" },
  { id: "violet", name: "Violet", primary: "#6B21A8", secondary: "#FAF5FF" },
];

// Categories mapping by boutique type
const CATEGORIES_BY_TYPE = {
  femme: [
    "Robes", "Tops & Blouses", "Pantalons", "Jupes", "Vestes & Manteaux",
    "Lingerie", "Maillots de bain", "Accessoires", "Sacs", "Chaussures"
  ],
  homme: [
    "T-shirts", "Chemises", "Pantalons", "Costumes & Blazers", "Vestes",
    "Sous-vêtements", "Chaussures", "Accessoires", "Sacs & Sacoches"
  ],
  enfant: [
    "Bodies & Pyjamas", "Hauts", "Bas & Pantalons", "Robes & Jupes",
    "Manteaux", "Chaussures enfant", "Accessoires", "Cartables & Sacs"
  ],
  sneakers: [
    "Sneakers", "Chaussures de ville", "Sandales", "Bottes & Bottines",
    "Casquettes & Chapeaux", "Ceintures", "Lunettes", "Montres",
    "Bijoux fantaisie", "Sacs"
  ],
  bijoux: [
    "Colliers", "Bracelets", "Bagues", "Boucles d'oreilles", "Montres femme",
    "Montres homme", "Bijoux enfant", "Parures", "Bijoux en or",
    "Bijoux en argent", "Fantaisie"
  ],
  boissons: [
    "Vins", "Bières", "Liqueurs & Spiritueux", "Champagnes",
    "Boissons sans alcool", "Sucreries", "Snacks & Apéritifs"
  ],
  restaurant: [
    "Entrées", "Plats", "Desserts", "Menus", "Accompagnements", "Boissons", "Boissons chaudes"
  ],
  bar: [
    "Bières", "Cocktails", "Vins", "Softs", "Cafés & Thés", "Snacks"
  ],
  epicerie: [
    "Frais", "Fruits & Légumes", "Épicerie sèche", "Boissons", "Boulangerie", "Hygiène & Entretien", "Surgelés"
  ],
  general: [
    "Articles", "Accessoires", "Services", "Divers"
  ],
  multi: [
    "Femme", "Homme", "Enfant", "Chaussures", "Bijoux & Montres",
    "Sacs", "Accessoires", "Lingerie", "Sport", "Autres"
  ]
};

export default function BoutiqueOSOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState("next");
  const [isAnimating, setIsAnimating] = useState(false);
  const fileInputRef = useRef(null);
  const colorInputRef = useRef(null);

  // Load Inter + JetBrains Mono fonts (matching landing page)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1
    boutiqueName: "",
    slug: "",
    slugEdited: false,
    boutiqueType: "",
    categories: [], // Categories based on type
    // Step 2
    country: null,
    currency: "",
    whatsapp: "",
    // Step 3
    logo: null,
    logoPreview: null,
    colorPalette: "luxe",
    customColor: null,
    tagline: "",
    // Step 4
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // Slug availability
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null });
  
  // Categories UI state
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [categoryAnimations, setCategoryAnimations] = useState([]);
  
  // Country dropdown
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const countryDropdownRef = useRef(null);
  
  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Auto-generate slug from name
  useEffect(() => {
    if (!formData.slugEdited && formData.boutiqueName) {
      const newSlug = generateSlug(formData.boutiqueName);
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
  }, [formData.boutiqueName, formData.slugEdited]);

  // Check slug availability (real API call)
  useEffect(() => {
    if (formData.slug && formData.slug.length >= 2) {
      setSlugStatus({ checking: true, available: null });
      const timer = setTimeout(async () => {
        try {
          const response = await axios.get(`${API}/boutiqueos/check-slug/${formData.slug}`);
          setSlugStatus({ checking: false, available: response.data.available });
        } catch (error) {
          console.error("Error checking slug:", error);
          setSlugStatus({ checking: false, available: null });
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setSlugStatus({ checking: false, available: null });
    }
  }, [formData.slug]);

  // Auto-fill currency when country changes
  useEffect(() => {
    if (formData.country) {
      setFormData(prev => ({ ...prev, currency: formData.country.currency }));
    }
  }, [formData.country]);

  // Load categories when boutique type changes
  useEffect(() => {
    if (formData.boutiqueType && CATEGORIES_BY_TYPE[formData.boutiqueType]) {
      const categories = [...CATEGORIES_BY_TYPE[formData.boutiqueType]];
      setFormData(prev => ({ ...prev, categories }));
      
      // Animate categories appearing
      setCategoryAnimations([]);
      categories.forEach((_, index) => {
        setTimeout(() => {
          setCategoryAnimations(prev => [...prev, index]);
        }, index * 50);
      });
    }
  }, [formData.boutiqueType]);

  // Handle category removal
  const removeCategory = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, index) => index !== indexToRemove)
    }));
    setCategoryAnimations(prev => prev.filter(i => i !== indexToRemove));
  };

  // Handle adding new category
  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      const newCategories = [...formData.categories, newCategory.trim()];
      setFormData(prev => ({ ...prev, categories: newCategories }));
      setCategoryAnimations(prev => [...prev, newCategories.length - 1]);
      setNewCategory("");
      setShowCategoryInput(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlugChange = (value) => {
    const cleanSlug = generateSlug(value);
    setFormData(prev => ({ ...prev, slug: cleanSlug, slugEdited: true }));
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => {
      // Adapter le préfixe téléphonique au pays sélectionné
      let newWhatsapp = prev.whatsapp || "";
      const oldPrefix = prev.country?.phoneCode;
      if (!newWhatsapp || newWhatsapp.trim() === "" || newWhatsapp.trim() === oldPrefix) {
        // Champ vide ou seulement l'ancien préfixe → mettre le nouveau
        newWhatsapp = country.phoneCode + " ";
      } else if (oldPrefix && newWhatsapp.startsWith(oldPrefix)) {
        // Remplacer l'ancien préfixe par le nouveau
        newWhatsapp = country.phoneCode + newWhatsapp.slice(oldPrefix.length);
      } else if (!newWhatsapp.startsWith("+")) {
        // Pas de préfixe → ajouter le nouveau
        newWhatsapp = country.phoneCode + " " + newWhatsapp.trim();
      }
      return { ...prev, country, whatsapp: newWhatsapp };
    });
    setCountryDropdownOpen(false);
    setCountrySearch("");
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorSelect = (paletteId) => {
    setFormData(prev => ({ 
      ...prev, 
      colorPalette: paletteId,
      customColor: null 
    }));
  };

  const handleCustomColor = (e) => {
    const color = e.target.value;
    setFormData(prev => ({ 
      ...prev, 
      customColor: color,
      colorPalette: null 
    }));
  };

  const getActiveColor = () => {
    if (formData.customColor) return formData.customColor;
    const palette = COLOR_PALETTES.find(p => p.id === formData.colorPalette);
    return palette ? palette.primary : "#D4AF37";
  };

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password strength calculator
  const getPasswordStrength = (password) => {
    if (!password) return null;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { label: "Faible", color: "#EF4444", level: 1 };
    if (strength <= 3) return { label: "Moyen", color: "#F59E0B", level: 2 };
    return { label: "Fort", color: "#22C55E", level: 3 };
  };

  const getFilteredCountries = () => {
    if (!countrySearch) return COUNTRIES;
    const search = countrySearch.toLowerCase();
    return COUNTRIES.filter(c => 
      c.name.toLowerCase().includes(search) || 
      c.code.toLowerCase().includes(search)
    );
  };

  const getGroupedCountries = () => {
    const filtered = getFilteredCountries();
    const groups = {};
    filtered.forEach(country => {
      if (!groups[country.group]) {
        groups[country.group] = [];
      }
      groups[country.group].push(country);
    });
    return groups;
  };

  const goToStep = (step) => {
    if (isAnimating || step === currentStep) return;
    setDirection(step > currentStep ? "next" : "prev");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(step);
      setIsAnimating(false);
    }, 350);
  };

  const nextStep = () => {
    if (currentStep < 5 && canProceed()) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    } else {
      navigate("/");
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.boutiqueName.trim() && formData.slug && formData.boutiqueType && slugStatus.available;
      case 2:
        return formData.country && formData.currency && formData.whatsapp.trim().length > 5;
      case 3:
        return true; // Logo and tagline are optional
      case 4:
        return formData.fullName.trim() && 
               isValidEmail(formData.email) && 
               formData.password.length >= 8 && 
               formData.password === formData.confirmPassword;
      case 5:
        return true;
      default:
        return false;
    }
  };

  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFinish = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Prepare data for API
      const registrationData = {
        boutique_name: formData.boutiqueName,
        slug: formData.slug,
        boutique_type: formData.boutiqueType,
        profile: BOUTIQUE_TYPES.find(t => t.id === formData.boutiqueType)?.family || "mode",
        categories: formData.categories, // Include categories
        country_code: formData.country?.code || "",
        country_name: formData.country?.name || "",
        currency: formData.currency,
        currency_symbol: formData.country?.currencySymbol || "",
        whatsapp: formData.whatsapp,
        logo: formData.logoPreview || null,
        color_palette: formData.colorPalette,
        custom_color: formData.customColor,
        tagline: formData.tagline,
        admin_name: formData.fullName,
        admin_email: formData.email,
        admin_password: formData.password
      };
      
      // Call API to register boutique
      const response = await axios.post(`${API}/boutiqueos/register`, registrationData);
      
      // Store token and user data
      localStorage.setItem("token", response.data.access_token);
      
      // [VALIDATION-FIX] Sauvegarder compteValide depuis MongoDB (toujours false à la création)
      sessionStorage.setItem("compteValide", "false");
      
      // Store boutique data in localStorage for sidebar personalization
      const boutiqueData = {
        nom: formData.boutiqueName,
        slug: formData.slug,
        type: formData.boutiqueType,
        profile: BOUTIQUE_TYPES.find(t => t.id === formData.boutiqueType)?.family || "mode",
        categories: formData.categories,
        logo: formData.logoPreview || null,
        colorPalette: formData.colorPalette,
        customColor: formData.customColor,
        currency: formData.currency,
        currencySymbol: formData.country?.currencySymbol || "€",
        whatsapp: formData.whatsapp,
        tagline: formData.tagline,
        adminName: formData.fullName,
        adminEmail: formData.email
      };
      localStorage.setItem("boutique", JSON.stringify(boutiqueData));
      
      // Show success message
      toast.success(`Bienvenue ${formData.fullName.split(" ")[0]} ! Votre boutique est créée.`);
      
      // [VALIDATION-FIX] Rediriger vers la caisse (seul module accessible)
      setTimeout(() => {
        navigate("/admin/sales");
      }, 1500);
      
    } catch (error) {
      console.error("Registration error:", error);
      const message = error.response?.data?.detail || "Erreur lors de la création de votre boutique";
      toast.error(message);
      setIsSubmitting(false);
    }
  };

  const getCurrencyDisplay = (currencyCode) => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    if (currency) {
      return `${currency.symbol} (${currency.code})`;
    }
    return currencyCode;
  };

  return (
    <div className="onboarding-container">
      {/* Grid texture applied via CSS ::before */}

      {/* ── Back button — glass pour toutes les étapes ── */}
      <button className="back-btn-glass" onClick={prevStep}>← Retour</button>

      {/* Steps Container */}
      <div className="steps-wrapper steps-wrapper-step1">
        <div className={`step-content ${isAnimating ? (direction === "next" ? "slide-out-left" : "slide-out-right") : ""}`}>
          
          {/* ── Step 1 — Glassmorphism Card ── */}
          {currentStep === 1 && (
            <div className="step1-layout">
              <div className="glass-card">
                {/* Top luminous edge line via ::before pseudo */}

                {/* Glass Stepper */}
                <div className="glass-stepper">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <div key={s} className={`glass-dot ${s === currentStep ? "glass-dot-active" : "glass-dot-inactive"}`} />
                  ))}
                </div>

                {/* Icon */}
                <div className="glass-step-icon">🏪</div>

                {/* Title & Subtitle */}
                <h1 className="glass-title">Votre boutique</h1>
                <p className="glass-subtitle">Commençons par les bases</p>

                {/* Form fields */}
                <div className="glass-form-fields">

                  {/* Boutique name */}
                  <div className="glass-field-group">
                    <label className="glass-label">Nom de la boutique *</label>
                    <input
                      type="text"
                      value={formData.boutiqueName}
                      onChange={(e) => handleInputChange("boutiqueName", e.target.value)}
                      placeholder="Ex: Mode Élégance Paris"
                      className="glass-input"
                    />
                  </div>

                  {/* Slug — read-only by default, editable on demand */}
                  {formData.slug && (
                    <div className="glass-field-group">
                      <div className="glass-slug-readonly">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'rgba(255,255,255,0.4)' }}>
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                        <span className="glass-slug-url">
                          boutiqueOS.com/<strong>{formData.slug}</strong>
                        </span>
                        {slugStatus.checking ? (
                          <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: 'auto' }}>…</span>
                        ) : slugStatus.available === true ? (
                          <span style={{ fontSize: '11px', color: '#34D399', marginLeft: 'auto' }}>✓ Disponible</span>
                        ) : slugStatus.available === false ? (
                          <span style={{ fontSize: '11px', color: '#F87171', marginLeft: 'auto' }}>Déjà pris</span>
                        ) : null}
                      </div>
                      {slugStatus.available === false && (
                        <div className="glass-slug-wrapper" style={{ marginTop: '8px' }}>
                          <span className="glass-slug-prefix">boutiqueOS.com/</span>
                          <input
                            type="text"
                            value={formData.slug}
                            onChange={(e) => handleSlugChange(e.target.value)}
                            className="glass-slug-input"
                            placeholder="ma-boutique"
                            autoFocus
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Type de boutique — groupé par famille (Caisse universelle / Boutique mode) */}
                  <div className="glass-field-group">
                    <label className="glass-label">Type de commerce *</label>
                    {TYPE_FAMILIES.map((family) => (
                      <div key={family.id} className="type-family-block" data-testid={`type-family-${family.id}`}>
                        <div className="type-family-header">
                          <span className="type-family-name">{family.label}</span>
                          <span className="type-family-desc">{family.desc}</span>
                        </div>
                        <div className="glass-type-grid">
                          {BOUTIQUE_TYPES.filter((t) => t.family === family.id).map((type) => (
                            <button
                              key={type.id}
                              type="button"
                              className={`glass-type-card ${formData.boutiqueType === type.id ? "selected-type" : ""}`}
                              onClick={() => handleInputChange("boutiqueType", type.id)}
                              data-testid={`boutique-type-${type.id}`}
                            >
                              <div className="type-check-circle">✓</div>
                              <div className="type-content-row">
                                <div className="type-icon-box">{type.icon}</div>
                                <div className="type-text-block">
                                  <div className="type-text-name">{type.label}</div>
                                  <div className="type-text-desc">{type.desc}</div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Categories — appears when type selected */}
                  {formData.boutiqueType && formData.categories.length > 0 && (
                    <div className="glass-field-group categories-section">
                      <label className="glass-label">Vos catégories par défaut :</label>
                      <div className="categories-container">
                        {formData.categories.map((category, index) => (
                          <div
                            key={`${category}-${index}`}
                            className={`category-badge ${categoryAnimations.includes(index) ? "animate-in" : ""}`}
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <span>{category}</span>
                            <button
                              className="remove-category"
                              onClick={() => removeCategory(index)}
                              type="button"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        {showCategoryInput ? (
                          <div className="add-category-input">
                            <input
                              type="text"
                              value={newCategory}
                              onChange={(e) => setNewCategory(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); addCategory(); }
                                else if (e.key === "Escape") { setShowCategoryInput(false); setNewCategory(""); }
                              }}
                              placeholder="Nom de la catégorie"
                              autoFocus
                            />
                            <button className="confirm-add" onClick={addCategory} type="button"><Check size={12} /></button>
                            <button className="cancel-add" onClick={() => { setShowCategoryInput(false); setNewCategory(""); }} type="button"><X size={12} /></button>
                          </div>
                        ) : (
                          <button className="add-category-btn" onClick={() => setShowCategoryInput(true)} type="button">
                            <Plus size={12} /><span>Ajouter</span>
                          </button>
                        )}
                      </div>
                      <p className="categories-hint">Vous pourrez modifier ces catégories à tout moment.</p>
                    </div>
                  )}
                </div>

                {/* Continue button — inside card */}
                <button
                  className={`glass-continue-btn ${!canProceed() ? "glass-continue-disabled" : ""}`}
                  onClick={nextStep}
                  disabled={!canProceed()}
                >
                  Continuer →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2 — Localisation & Devise ── */}
          {currentStep === 2 && (
            <div className="step1-layout">
              <div className="glass-card">
                <div className="glass-stepper">
                  {[1,2,3,4,5].map(s => (
                    <div key={s} className={`glass-dot ${s <= currentStep ? "glass-dot-active" : "glass-dot-inactive"}`} />
                  ))}
                </div>
                <div className="glass-step-icon">🌍</div>
                <h1 className="glass-title">Où êtes-vous ?</h1>
                <p className="glass-subtitle">Pour adapter la devise et les paramètres locaux</p>
                <div className="glass-form-fields">

                  {/* Pays */}
                  <div className="glass-field-group">
                    <label className="glass-label">Pays *</label>
                    <div className="country-dropdown" ref={countryDropdownRef}>
                      <button
                        className="glass-country-btn"
                        onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                      >
                        {formData.country ? (
                          <span className="country-selected">
                            <span className="country-flag">{formData.country.flag}</span>
                            <span>{formData.country.name}</span>
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.22)' }}>Sélectionnez votre pays</span>
                        )}
                        <ChevronDown size={16} style={{ color: 'rgba(255,255,255,0.35)', transition: 'transform 0.2s', transform: countryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                      </button>
                      {countryDropdownOpen && (
                        <div className="glass-dropdown-menu">
                          <div className="glass-search-wrapper">
                            <Search size={14} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                            <input
                              type="text"
                              placeholder="Rechercher un pays..."
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              className="glass-search-input"
                              autoFocus
                            />
                          </div>
                          <div className="glass-country-list">
                            {Object.entries(getGroupedCountries()).map(([group, countries]) => (
                              <div key={group} className="glass-country-group">
                                <div className="glass-group-label">{group}</div>
                                {countries.map((country) => (
                                  <button
                                    key={country.code}
                                    className={`glass-country-option ${formData.country?.code === country.code ? "selected" : ""}`}
                                    onClick={() => handleCountrySelect(country)}
                                  >
                                    <span>{country.flag}</span>
                                    <span style={{ flex: 1 }}>{country.name}</span>
                                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>{country.currencySymbol}</span>
                                  </button>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Devise */}
                  <div className="glass-field-group">
                    <label className="glass-label">Devise</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => handleInputChange("currency", e.target.value)}
                      className="glass-select"
                    >
                      <option value="">Sélectionnez une devise</option>
                      {CURRENCIES.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.symbol} ({currency.code}) - {currency.name}
                        </option>
                      ))}
                    </select>
                    {formData.currency && (
                      <div style={{ fontSize: '11px', color: '#22C55E', marginTop: '4px' }}>{getCurrencyDisplay(formData.currency)}</div>
                    )}
                  </div>

                  {/* WhatsApp */}
                  <div className="glass-field-group">
                    <label className="glass-label">Numéro WhatsApp boutique *</label>
                    <div className="glass-slug-wrapper">
                      <span className="glass-slug-prefix" data-testid="phone-prefix">
                        {formData.country ? `${formData.country.flag} ${formData.country.phoneCode}` : "+225"}
                      </span>
                      <input
                        type="tel"
                        value={formData.whatsapp.replace(formData.country?.phoneCode || '', '').trim()}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^\d\s]/g, '');
                          handleInputChange("whatsapp", (formData.country?.phoneCode || '+225') + " " + digits);
                        }}
                        placeholder="XX XX XX XX XX"
                        className="glass-slug-input"
                        data-testid="whatsapp-input"
                      />
                    </div>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '4px' }}>Vos clientes vous contacteront via ce numéro</span>
                  </div>
                </div>
                <button
                  className={`glass-continue-btn ${!canProceed() ? "glass-continue-disabled" : ""}`}
                  onClick={nextStep}
                  disabled={!canProceed()}
                >
                  Continuer →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3 — Identité visuelle ── */}
          {currentStep === 3 && (
            <div className="step1-layout">
              <div className="glass-card">
                <div className="glass-stepper">
                  {[1,2,3,4,5].map(s => (
                    <div key={s} className={`glass-dot ${s <= currentStep ? "glass-dot-active" : "glass-dot-inactive"}`} />
                  ))}
                </div>
                <div className="glass-step-icon">🎨</div>
                <h1 className="glass-title">Votre identité</h1>
                <p className="glass-subtitle">Personnalisez l'apparence de votre boutique</p>
                <div className="glass-form-fields">

                  {/* Logo */}
                  <div className="glass-field-group">
                    <label className="glass-label">Logo boutique</label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                    <button className="glass-logo-zone" onClick={() => fileInputRef.current?.click()}>
                      {formData.logoPreview ? (
                        <img src={formData.logoPreview} alt="Logo" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '10px' }} />
                      ) : (
                        <>
                          <Camera size={28} strokeWidth={1.5} style={{ color: 'rgba(255,255,255,0.3)' }} />
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginTop: '6px' }}>Logo (optionnel)</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Couleurs */}
                  <div className="glass-field-group">
                    <label className="glass-label">Couleur de votre boutique</label>
                    <div className="glass-color-grid">
                      {COLOR_PALETTES.map((palette) => (
                        <button
                          key={palette.id}
                          className={`glass-color-circle ${formData.colorPalette === palette.id ? "selected" : ""}`}
                          style={{ backgroundColor: palette.primary }}
                          onClick={() => handleColorSelect(palette.id)}
                          title={palette.name}
                        >
                          {formData.colorPalette === palette.id && (
                            <Check size={14} color={palette.id === 'nude' ? '#333' : '#fff'} />
                          )}
                        </button>
                      ))}
                      <button
                        className={`glass-color-circle custom-color ${formData.customColor ? "selected" : ""}`}
                        style={{ backgroundColor: formData.customColor || 'rgba(255,255,255,0.1)' }}
                        onClick={() => colorInputRef.current?.click()}
                        title="Couleur personnalisée"
                      >
                        {formData.customColor ? <Check size={14} color="#fff" /> : <Pipette size={14} color="rgba(255,255,255,0.5)" />}
                      </button>
                      <input ref={colorInputRef} type="color" value={formData.customColor || "#D4AF37"} onChange={handleCustomColor} style={{ display: 'none' }} />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="glass-preview-card" style={{ borderColor: getActiveColor() + '40' }}>
                    <div className="glass-preview-header" style={{ backgroundColor: getActiveColor() }}>
                      <span style={{ color: formData.colorPalette === 'nude' ? '#333' : '#fff', fontSize: '13px', fontWeight: '600' }}>
                        {formData.boutiqueName || "Ma Boutique"}
                      </span>
                    </div>
                    <div className="glass-preview-body">
                      <div className="glass-preview-product">
                        <div className="glass-preview-img" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' }}>Robe Été</div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: getActiveColor() }}>25 000 F</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tagline */}
                  <div className="glass-field-group">
                    <label className="glass-label">Tagline boutique (optionnel)</label>
                    <input
                      type="text"
                      value={formData.tagline}
                      onChange={(e) => handleInputChange("tagline", e.target.value.slice(0, 60))}
                      placeholder="Ex: Élégance au quotidien"
                      className="glass-input"
                      maxLength={60}
                    />
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '2px' }}>{formData.tagline.length}/60 caractères</span>
                  </div>
                </div>
                <button
                  className={`glass-continue-btn ${!canProceed() ? "glass-continue-disabled" : ""}`}
                  onClick={nextStep}
                  disabled={!canProceed()}
                >
                  Continuer →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4 — Votre compte ── */}
          {currentStep === 4 && (
            <div className="step1-layout">
              <div className="glass-card">
                <div className="glass-stepper">
                  {[1,2,3,4,5].map(s => (
                    <div key={s} className={`glass-dot ${s <= currentStep ? "glass-dot-active" : "glass-dot-inactive"}`} />
                  ))}
                </div>
                <div className="glass-step-icon">🔐</div>
                <h1 className="glass-title">Votre compte</h1>
                <p className="glass-subtitle">Pour accéder à votre tableau de bord</p>
                <div className="glass-form-fields">

                  {/* Nom */}
                  <div className="glass-field-group">
                    <label className="glass-label">Prénom et nom *</label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange("fullName", e.target.value)}
                      placeholder="Jean Dupont"
                      className="glass-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="glass-field-group">
                    <label className="glass-label">Email professionnel *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="contact@maboutique.com"
                      className={`glass-input ${formData.email && !isValidEmail(formData.email) ? "input-error" : ""}`}
                    />
                    {formData.email && !isValidEmail(formData.email) && (
                      <span style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>Format email invalide</span>
                    )}
                    {formData.email && isValidEmail(formData.email) && (
                      <span style={{ fontSize: '11px', color: '#22C55E', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={11} /> Email valide</span>
                    )}
                  </div>

                  {/* Mot de passe */}
                  <div className="glass-field-group">
                    <label className="glass-label">Mot de passe *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        placeholder="••••••••"
                        className="glass-input"
                        style={{ paddingRight: '44px' }}
                      />
                      <button type="button" className="glass-pwd-toggle" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {formData.password && (
                      <div className="glass-strength-row">
                        {[1,2,3].map(level => (
                          <div key={level} className="glass-strength-bar"
                            style={{ backgroundColor: getPasswordStrength(formData.password)?.level >= level ? getPasswordStrength(formData.password).color : 'rgba(255,255,255,0.1)' }}
                          />
                        ))}
                        <span style={{ fontSize: '11px', color: getPasswordStrength(formData.password)?.color, marginLeft: '6px' }}>
                          {getPasswordStrength(formData.password)?.label}
                        </span>
                      </div>
                    )}
                    {!formData.password && (
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '4px' }}>Minimum 8 caractères</span>
                    )}
                  </div>

                  {/* Confirmer */}
                  <div className="glass-field-group">
                    <label className="glass-label">Confirmer le mot de passe *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                        placeholder="••••••••"
                        className={`glass-input ${formData.confirmPassword && formData.password !== formData.confirmPassword ? "input-error" : ""}`}
                        style={{ paddingRight: '44px' }}
                      />
                      <button type="button" className="glass-pwd-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <span style={{ fontSize: '11px', color: '#EF4444', marginTop: '4px' }}>Les mots de passe ne correspondent pas</span>
                    )}
                    {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password && (
                      <span style={{ fontSize: '11px', color: '#22C55E', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={11} /> Mots de passe identiques</span>
                    )}
                  </div>
                </div>

                {/* Privacy */}
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', textAlign: 'center', marginBottom: '16px', lineHeight: '1.6' }}>
                  En continuant, vous acceptez nos{' '}
                  <a href="/terms" target="_blank" style={{ color: 'rgba(255,69,0,0.8)', textDecoration: 'none' }}>Conditions d'utilisation</a>
                  {' '}et notre{' '}
                  <a href="/privacy" target="_blank" style={{ color: 'rgba(255,69,0,0.8)', textDecoration: 'none' }}>Politique de confidentialité</a>
                </p>

                <button
                  className={`glass-continue-btn ${!canProceed() ? "glass-continue-disabled" : ""}`}
                  onClick={nextStep}
                  disabled={!canProceed()}
                >
                  Continuer →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 5 — Confirmation ── */}
          {currentStep === 5 && (
            <div className="step1-layout" style={{ paddingTop: '40px' }}>
              {/* Confetti behind the card */}
              <div className="confetti-container" style={{ zIndex: 0 }}>
                {[...Array(50)].map((_, i) => (
                  <div key={i} className="confetti"
                    style={{
                      left: `${Math.random() * 100}%`,
                      animationDelay: `${Math.random() * 2}s`,
                      backgroundColor: ['#FF4500','#6366F1','#F59E0B','#EC4899','#3B82F6','#8B5CF6','#22C55E','#FBBF24'][Math.floor(Math.random() * 8)]
                    }}
                  />
                ))}
              </div>

              <div className="glass-card" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                <div className="glass-stepper">
                  {[1,2,3,4,5].map(s => (
                    <div key={s} className={`glass-dot ${s <= currentStep ? "glass-dot-active" : "glass-dot-inactive"}`} />
                  ))}
                </div>

                {/* Success check */}
                <div className="glass-success-check">
                  <svg viewBox="0 0 100 100" width="72" height="72">
                    <circle cx="50" cy="50" r="45" className="circle-bg" />
                    <circle cx="50" cy="50" r="45" className="circle-progress" />
                    <path d="M30 50 L45 65 L70 35" className="check-mark" />
                  </svg>
                </div>

                <h1 className="glass-title" style={{ marginTop: '12px' }}>Votre boutique est prête ! 🎉</h1>
                <p className="glass-subtitle" style={{ marginBottom: '24px' }}>Tout est configuré, vous pouvez commencer</p>

                {/* Summary rows */}
                <div className="glass-summary-card">
                  <div className="glass-summary-row">
                    <span className="glass-summary-label">Boutique</span>
                    <span className="glass-summary-value">{formData.boutiqueName}</span>
                  </div>
                  <div className="glass-summary-row">
                    <span className="glass-summary-label">URL</span>
                    <span className="glass-summary-value" style={{ color: '#FF4500', fontSize: '12px' }}>boutiqueOS.com/{formData.slug}</span>
                  </div>
                  {formData.country && (
                    <div className="glass-summary-row">
                      <span className="glass-summary-label">Pays & Devise</span>
                      <span className="glass-summary-value">{formData.country.flag} {formData.country.name} · {getCurrencyDisplay(formData.currency)}</span>
                    </div>
                  )}
                  <div className="glass-summary-row" style={{ borderBottom: 'none' }}>
                    <span className="glass-summary-label">Type</span>
                    <span className="glass-summary-value">
                      {BOUTIQUE_TYPES.find(t => t.id === formData.boutiqueType)?.icon}{' '}
                      {BOUTIQUE_TYPES.find(t => t.id === formData.boutiqueType)?.label}
                    </span>
                  </div>
                </div>

                <button
                  className={`glass-continue-btn ${!canProceed() || isSubmitting ? "glass-continue-disabled" : ""}`}
                  onClick={handleFinish}
                  disabled={!canProceed() || isSubmitting}
                  style={{ marginTop: '20px' }}
                >
                  {isSubmitting ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <Loader2 size={16} className="spin-icon" /> Création en cours...
                    </span>
                  ) : (
                    'Accéder à mon tableau de bord →'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Continue Button — toujours caché (bouton dans chaque card) */}
      <div className="button-container btn-hidden">
        <button
          className={`continue-btn ${!canProceed() || isSubmitting ? "disabled" : ""}`}
          onClick={currentStep === 5 ? handleFinish : nextStep}
          disabled={!canProceed() || isSubmitting}
        >
          {currentStep === 5 ? (
            isSubmitting ? (
              <>
                <Loader2 size={20} className="spin-icon" /> Création en cours...
              </>
            ) : (
              "Accéder à mon tableau de bord →"
            )
          ) : (
            "Continuer"
          )}
        </button>
      </div>

      <style>{`
        /* ─── Design System Tokens (matching BoutiqueOSWelcome.jsx) ─── */
        .onboarding-container {
          min-height: 100vh;
          background: #0A0A0A;
          background-image: url('/glaze_bg.webp');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          display: flex;
          flex-direction: column;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          position: relative;
          overflow-x: hidden;
        }

        /* Subtle grid texture (matching landing page) */
        .onboarding-container::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 64px 64px;
          pointer-events: none;
          z-index: 0;
        }

        /* ─── Back button ─── */
        .back-btn-glass {
          position: fixed;
          top: 20px;
          left: 20px;
          z-index: 200;
          background: none;
          border: none;
          color: #6B7280;
          font-size: 13px;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          padding: 6px 4px;
          transition: color 150ms cubic-bezier(0.4,0,0.2,1);
          letter-spacing: -0.01em;
        }
        .back-btn-glass:hover { color: #9CA3AF; }

        /* ─── Steps wrapper ─── */
        .steps-wrapper {
          flex: 1;
          padding: 80px 24px 120px;
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
          z-index: 1;
        }
        .steps-wrapper-step1 {
          padding: 52px 16px 32px;
        }

        .step-content {
          max-width: 440px;
          margin: 0 auto;
          transition: transform 0.35s ease-in-out, opacity 0.35s ease-in-out;
        }
        .step-content.slide-out-left { transform: translateX(-100%); opacity: 0; }
        .step-content.slide-out-right { transform: translateX(100%); opacity: 0; }

        .step { animation: slideIn 0.35s ease-in-out; }
        @keyframes slideIn {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* ─── Layout wrapper ─── */
        .step1-layout {
          display: flex;
          justify-content: center;
          padding: 20px 0 40px;
        }

        /* ─── Card (flat dark — matching landing page modal) ─── */
        .glass-card {
          position: relative;
          width: 100%;
          max-width: 420px;
          background: #111111;
          border: 1px solid #2A2A2A;
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.5);
          padding: 28px 24px 24px;
          overflow: hidden;
        }

        /* Subtle top accent line */
        .glass-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 60%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,69,0,0.4), transparent);
        }

        /* ─── Stepper ─── */
        .glass-stepper {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          margin-bottom: 24px;
        }
        .glass-dot {
          height: 3px;
          border-radius: 3px;
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        .glass-dot-active {
          width: 28px;
          background: #FF4500;
        }
        .glass-dot-inactive {
          width: 18px;
          background: #2A2A2A;
        }

        /* ─── Step icon ─── */
        .glass-step-icon {
          width: 48px;
          height: 48px;
          background: rgba(255,69,0,0.1);
          border: 1px solid rgba(255,69,0,0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          margin: 0 auto 16px;
        }

        /* ─── Title & Subtitle ─── */
        .glass-title {
          font-size: 20px;
          font-weight: 600;
          color: #FFFFFF;
          letter-spacing: -0.02em;
          text-align: center;
          margin: 0 0 4px;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .glass-subtitle {
          font-size: 13px;
          color: #6B7280;
          text-align: center;
          margin: 0 0 24px;
          line-height: 1.5;
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* ─── Form fields ─── */
        .glass-form-fields {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 24px;
        }
        .glass-field-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .glass-label {
          font-size: 12px;
          font-weight: 500;
          color: #9CA3AF;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* ─── Input fields (matching landing page modal) ─── */
        .glass-input {
          width: 100%;
          box-sizing: border-box;
          background: #0A0A0A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          padding: 11px 14px;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 14px;
          color: #FFFFFF;
          outline: none;
          transition: border-color 150ms cubic-bezier(0.4,0,0.2,1), box-shadow 150ms cubic-bezier(0.4,0,0.2,1);
          -webkit-appearance: none;
        }
        .glass-input::placeholder { color: #6B7280; }
        .glass-input:focus {
          border-color: #FF4500;
          box-shadow: 0 0 0 3px rgba(255,69,0,0.15);
        }
        .glass-input.input-error {
          border-color: #EF4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.12);
        }

        /* ─── Slug display ─── */
        .glass-slug-wrapper {
          display: flex;
          align-items: center;
          background: #0A0A0A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          overflow: hidden;
          transition: border-color 150ms, box-shadow 150ms;
        }
        .glass-slug-wrapper:focus-within {
          border-color: #FF4500;
          box-shadow: 0 0 0 3px rgba(255,69,0,0.15);
        }
        .glass-slug-prefix {
          padding: 11px 2px 11px 14px;
          font-size: 13px;
          color: #6B7280;
          white-space: nowrap;
          user-select: none;
        }
        .glass-slug-input {
          flex: 1;
          background: transparent;
          border: none;
          padding: 11px 14px 11px 4px;
          font-size: 14px;
          color: #FFFFFF;
          font-family: 'Inter', system-ui, sans-serif;
          outline: none;
        }

        /* Slug readonly display */
        .glass-slug-readonly {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #1A1A1A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          font-size: 12px;
          color: #6B7280;
        }
        .glass-slug-url {
          flex: 1;
          font-size: 12px;
          color: #6B7280;
        }
        .glass-slug-url strong {
          color: #FFFFFF;
          font-weight: 500;
        }

        /* ─── Type grid ─── */
        .type-family-block { margin-bottom: 14px; }
        .type-family-block:last-child { margin-bottom: 0; }
        .type-family-header {
          display: flex;
          align-items: baseline;
          gap: 8px;
          margin-bottom: 8px;
          padding-left: 2px;
        }
        .type-family-name {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #FF4500;
        }
        .type-family-desc {
          font-size: 11px;
          color: #6B7280;
        }
        .glass-type-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }
        .glass-type-card {
          position: relative;
          background: #1A1A1A;
          border: 1px solid #2A2A2A;
          border-radius: 12px;
          padding: 14px 10px;
          cursor: pointer;
          transition: background 150ms cubic-bezier(0.4,0,0.2,1), border-color 150ms cubic-bezier(0.4,0,0.2,1);
          overflow: hidden;
          text-align: left;
        }
        .glass-type-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: 80%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,69,0,0.5), transparent);
          transition: transform 0.3s ease;
        }
        .glass-type-card:hover:not(.selected-type) {
          background: #222222;
          border-color: #3A3A3A;
        }
        .glass-type-card.selected-type {
          background: rgba(255,69,0,0.06);
          border-color: rgba(255,69,0,0.35);
        }
        .glass-type-card.selected-type::before {
          transform: translateX(-50%) scaleX(1);
        }

        /* Checkmark circle */
        .type-check-circle {
          position: absolute;
          top: 7px;
          right: 7px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #FF4500;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transform: scale(0);
          transition: opacity 0.2s ease, transform 0.2s ease;
          font-size: 8px;
          color: #FFFFFF;
          font-weight: 800;
          line-height: 1;
        }
        .glass-type-card.selected-type .type-check-circle {
          opacity: 1;
          transform: scale(1);
        }

        /* Type card content */
        .type-content-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding-right: 12px;
        }
        .type-icon-box {
          width: 36px;
          height: 36px;
          background: #2A2A2A;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .glass-type-card.selected-type .type-icon-box {
          background: rgba(255,69,0,0.15);
        }
        .type-text-block { flex: 1; min-width: 0; }
        .type-text-name {
          font-size: 12px;
          font-weight: 600;
          color: rgba(255,255,255,0.85);
          line-height: 1.3;
          transition: color 0.2s;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        .glass-type-card.selected-type .type-text-name { color: #FF4500; }
        .type-text-desc {
          font-size: 10px;
          color: #6B7280;
          line-height: 1.3;
          margin-top: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ─── Continue button (white CTA — matching landing page) ─── */
        .glass-continue-btn {
          width: 100%;
          background: #FFFFFF;
          color: #0A0A0A;
          border: none;
          border-radius: 10px;
          padding: 13px;
          font-size: 14px;
          font-weight: 600;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: opacity 150ms cubic-bezier(0.4,0,0.2,1), transform 150ms cubic-bezier(0.4,0,0.2,1);
          letter-spacing: -0.01em;
        }
        .glass-continue-btn:hover:not(.glass-continue-disabled) {
          opacity: 0.88;
        }
        .glass-continue-btn:active:not(.glass-continue-disabled) {
          transform: scale(0.98);
        }
        .glass-continue-btn.glass-continue-disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        /* ─── Categories ─── */
        .categories-section { margin-top: 8px; }
        .categories-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }
        .category-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #1A1A1A;
          border: 1px solid #2A2A2A;
          border-radius: 20px;
          padding: 7px 12px;
          font-size: 13px;
          color: rgba(255,255,255,0.8);
          opacity: 0;
          transform: scale(0.8);
          animation: categoryFadeIn 0.3s ease forwards;
        }
        .category-badge.animate-in {
          opacity: 1;
          transform: scale(1);
        }
        @keyframes categoryFadeIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .remove-category {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          background: rgba(255,255,255,0.08);
          border: none;
          border-radius: 50%;
          color: #6B7280;
          cursor: pointer;
          transition: all 0.2s;
        }
        .remove-category:hover {
          background: rgba(239,68,68,0.15);
          color: #EF4444;
        }
        .add-category-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: 1px dashed #3A3A3A;
          border-radius: 20px;
          padding: 7px 12px;
          font-size: 13px;
          color: #6B7280;
          cursor: pointer;
          transition: all 0.2s;
        }
        .add-category-btn:hover {
          border-color: rgba(255,69,0,0.4);
          color: #FF4500;
        }
        .add-category-input {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #1A1A1A;
          border: 1px solid #3A3A3A;
          border-radius: 20px;
          padding: 4px 8px 4px 14px;
        }
        .add-category-input input {
          width: 120px;
          background: transparent;
          border: none;
          font-size: 13px;
          color: #FFFFFF;
          outline: none;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .add-category-input input::placeholder { color: #6B7280; }
        .confirm-add, .cancel-add {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
        }
        .confirm-add {
          background: rgba(34,197,94,0.15);
          color: #22C55E;
        }
        .confirm-add:hover { background: rgba(34,197,94,0.25); }
        .cancel-add {
          background: rgba(239,68,68,0.15);
          color: #EF4444;
        }
        .cancel-add:hover { background: rgba(239,68,68,0.25); }
        .categories-hint {
          font-size: 12px;
          color: #6B7280;
          margin-top: 10px;
        }

        /* ─── Success check (Step 5) ─── */
        .glass-success-check {
          display: flex;
          justify-content: center;
          margin: 8px 0 4px;
        }
        .check-circle { width: 100%; height: 100%; }
        .circle-bg {
          fill: none;
          stroke: #2A2A2A;
          stroke-width: 3;
        }
        .circle-progress {
          fill: none;
          stroke: #FF4500;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-dasharray: 283;
          stroke-dashoffset: 283;
          animation: drawCircle 0.6s ease-out forwards;
          transform: rotate(-90deg);
          transform-origin: center;
        }
        .check-mark {
          fill: none;
          stroke: #FFFFFF;
          stroke-width: 4;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 60;
          stroke-dashoffset: 60;
          animation: drawCheck 0.4s ease-out 0.5s forwards;
        }
        @keyframes drawCircle {
          to { stroke-dashoffset: 0; }
        }
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }

        /* ─── Confetti ─── */
        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
          z-index: 10;
        }
        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          top: -20px;
          opacity: 0;
          animation: confettiFall 3s ease-in-out forwards;
        }
        .confetti:nth-child(odd) { border-radius: 50%; }
        .confetti:nth-child(even) { border-radius: 2px; transform: rotate(45deg); }
        @keyframes confettiFall {
          0% { opacity: 1; top: -20px; transform: translateX(0) rotate(0deg); }
          100% { opacity: 0; top: 400px; transform: translateX(100px) rotate(720deg); }
        }

        /* ─── Summary card (Step 5) ─── */
        .glass-summary-card {
          background: #1A1A1A;
          border: 1px solid #2A2A2A;
          border-radius: 12px;
          overflow: hidden;
          text-align: left;
        }
        .glass-summary-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 11px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          gap: 12px;
        }
        .glass-summary-label {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6B7280;
          white-space: nowrap;
          font-family: 'JetBrains Mono', monospace;
        }
        .glass-summary-value {
          font-size: 13px;
          font-weight: 500;
          color: rgba(255,255,255,0.85);
          text-align: right;
        }

        /* ─── Spin icon ─── */
        .spin-icon {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ─── Hidden global button container ─── */
        .button-container {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 20px 24px;
          padding-bottom: max(20px, env(safe-area-inset-bottom));
          background: linear-gradient(to top, #0A0A0A 80%, transparent);
          z-index: 50;
        }
        .button-container.btn-hidden { display: none; }
        .continue-btn {
          width: 100%;
          max-width: 440px;
          margin: 0 auto;
          display: block;
          background: #FFFFFF;
          color: #0A0A0A;
          border: none;
          border-radius: 10px;
          padding: 14px 32px;
          font-size: 15px;
          font-weight: 600;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: opacity 150ms, transform 150ms;
        }
        .continue-btn:hover:not(.disabled) { opacity: 0.88; }
        .continue-btn:active:not(.disabled) { transform: scale(0.98); }
        .continue-btn.disabled { opacity: 0.35; cursor: not-allowed; }
        .continue-btn .spin-icon {
          animation: spin 1s linear infinite;
          display: inline-block;
          margin-right: 8px;
        }

        /* ─── Country dropdown (Step 2) ─── */
        .country-dropdown { position: relative; }
        .glass-country-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #0A0A0A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          padding: 11px 14px;
          color: #FFFFFF;
          font-size: 14px;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: border-color 150ms;
          text-align: left;
        }
        .glass-country-btn:hover { border-color: #3A3A3A; }
        .country-selected { display: flex; align-items: center; gap: 10px; }
        .country-flag { font-size: 18px; }

        .glass-dropdown-menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: #111111;
          border: 1px solid #2A2A2A;
          border-radius: 12px;
          overflow: hidden;
          z-index: 300;
          box-shadow: 0 16px 40px rgba(0,0,0,0.7);
        }
        .glass-search-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-bottom: 1px solid #2A2A2A;
        }
        .glass-search-input {
          flex: 1;
          background: none;
          border: none;
          color: #FFFFFF;
          font-size: 13px;
          font-family: 'Inter', system-ui, sans-serif;
          outline: none;
        }
        .glass-search-input::placeholder { color: #6B7280; }
        .glass-country-list { max-height: 220px; overflow-y: auto; }
        .glass-country-list::-webkit-scrollbar { width: 4px; }
        .glass-country-list::-webkit-scrollbar-thumb { background: #2A2A2A; border-radius: 2px; }
        .glass-country-group { padding: 4px 0; }
        .glass-group-label {
          padding: 6px 12px 4px;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #6B7280;
          font-family: 'JetBrains Mono', monospace;
        }
        .glass-country-option {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          background: none;
          border: none;
          color: #9CA3AF;
          font-size: 13px;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          text-align: left;
          transition: background 150ms, color 150ms;
        }
        .glass-country-option:hover { background: #1A1A1A; color: #FFFFFF; }
        .glass-country-option.selected { background: rgba(255,69,0,0.08); color: #FF4500; }

        /* ─── Currency select (Step 2) ─── */
        .glass-select {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          padding: 11px 14px;
          color: #FFFFFF;
          font-size: 14px;
          font-family: 'Inter', system-ui, sans-serif;
          outline: none;
          cursor: pointer;
          transition: border-color 150ms, box-shadow 150ms;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,0.3)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 36px;
        }
        .glass-select:focus {
          border-color: #FF4500;
          box-shadow: 0 0 0 3px rgba(255,69,0,0.15);
        }
        .glass-select option { background: #111111; color: #FFFFFF; }

        /* ─── Logo upload zone (Step 3) ─── */
        .glass-logo-zone {
          width: 100%;
          height: 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #1A1A1A;
          border: 1px dashed #3A3A3A;
          border-radius: 12px;
          cursor: pointer;
          transition: border-color 150ms, background 150ms;
          gap: 6px;
        }
        .glass-logo-zone:hover {
          border-color: rgba(255,69,0,0.4);
          background: rgba(255,69,0,0.04);
        }

        /* ─── Color circles (Step 3) ─── */
        .glass-color-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .glass-color-circle {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 2px solid transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
        }
        .glass-color-circle:hover { transform: scale(1.1); }
        .glass-color-circle.selected {
          border-color: #FFFFFF;
          transform: scale(1.1);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.1);
        }
        .glass-color-circle.custom-color {
          border: 2px dashed #3A3A3A;
          background: #1A1A1A !important;
        }
        .glass-color-circle.custom-color.selected { border-style: solid; border-color: #FFFFFF; }

        /* ─── Preview card (Step 3) ─── */
        .glass-preview-card {
          border: 1px solid;
          border-radius: 12px;
          overflow: hidden;
          transition: border-color 0.3s;
        }
        .glass-preview-header {
          padding: 10px 14px;
          transition: background-color 0.3s;
        }
        .glass-preview-body {
          background: #1A1A1A;
          padding: 12px 14px;
        }
        .glass-preview-product {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .glass-preview-img {
          width: 44px;
          height: 44px;
          border-radius: 8px;
          background: #2A2A2A;
        }

        /* ─── Password toggle (Step 4) ─── */
        .glass-pwd-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6B7280;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          transition: color 150ms;
        }
        .glass-pwd-toggle:hover { color: #9CA3AF; }

        /* ─── Password strength (Step 4) ─── */
        .glass-strength-row {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 8px;
        }
        .glass-strength-bar {
          flex: 1;
          height: 3px;
          border-radius: 2px;
          transition: background-color 0.3s;
        }

        /* ─── Responsive ─── */
        @media (max-width: 480px) {
          .glass-card { padding: 24px 16px 20px; border-radius: 14px; }
          .glass-title { font-size: 18px; }
          .glass-type-grid { gap: 6px; }
          .type-text-name { font-size: 11px; }
          .type-text-desc { display: none; }
          .type-icon-box { width: 30px; height: 30px; font-size: 15px; }
          .glass-continue-btn { padding: 12px; font-size: 13px; }
          .glass-step-icon { width: 40px; height: 40px; font-size: 18px; }
        }
      `}</style>
    </div>
  );
}
